import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar, LinePath, Line } from '@visx/shape';
import { localPoint } from '@visx/event';
import PropTypes from 'prop-types';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths,
  printFormat,
  financialMonth,
} from '../../../../Utils/Functions';
import {
  fMonths,
  enMonths,
  positiveActiveColor,
  activeLabelColor,
  negativeActiveColor,
} from '../../../../Assets/js/constant';

let tooltipTimeout;
const xSelector = (d) => d.month;
const ySelector = (d) => d.CashBalance;

const getSelectedDimDate = (item, defaultStartMonth) =>
  printFormat.dateYYYYMMDD(
    item.month + 1 >= defaultStartMonth ? item.year - 1 : item.year,
    item.month + 1,
  );

const TopChart = ({
  topData,
  selectedYears,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  updateFilter,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();
  const dragChartRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [data, setData] = useState([]);
  const [safetyLine, setSafetyLine] = useState(0);
  const [maxLine, setMaxLine] = useState(0);
  const [minLine, setMinLine] = useState(0);
  const [xTransform, setXTransform] = useState(0);
  const [X, setX] = useState(0);
  const [iX, setIX] = useState(0);
  const [iY, setIY] = useState(0);
  const [rX, setRX] = useState(0);
  const [rY, setRY] = useState(0);
  const [rW, setRW] = useState(0);
  const [rH, setRH] = useState(0);
  const [canvasIndex, setCanvasIndex] = useState(-1);
  const [dragFlag, setDragFlag] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const handleResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    prepareData();
  }, [topData, selectedYears]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let newData = [];
    let _safetyLine = 0;
    let _maxLine = 0;
    let _minLine = 0;

    if (selectedYears.length === 0) return;

    selectedYears.forEach((year) => {
      dictData[year] = {};
      dictData[year]['months'] = [];
      dictData[year]['totals'] = [];
      dictData[year]['year'] = year;
      fMonths.forEach((month) => {
        dictData[year]['months'][month] = {};
        dictData[year]['months'][month]['month'] = month;
        dictData[year]['months'][month]['year'] = year;
        dictData[year]['months'][month]['CashBalance'] = 0;
        dictData[year]['months'][month]['SafetyBankBalance'] = 0;
        dictData[year]['totals'][month] = 0;
      });
    });

    let fiscalMonths = getMonths(defaultStartMonth);

    if (topData && topData.length > 0) {
      topData.forEach((item) => {
        let month = getMonth(item.Date);

        if (dictData[item.FY]) {
          if (
            dictData[item.FY]['months'][month] !== null &&
            dictData[item.FY]['months'][month] !== undefined
          ) {
            if (topData.filter((data) => data.Date === item.Date).length > 1) {
              if (item.ActualFlag) {
                dictData[item.FY]['months'][month]['CashBalance'] = parseFloat(
                  item.CashBalance,
                );
                dictData[item.FY]['months'][month]['SafetyBankBalance'] =
                  item.SafetyBankBalance
                    ? parseFloat(item.SafetyBankBalance)
                    : 0;
                dictData[item.FY]['totals'][month] = parseFloat(
                  item.CashBalance,
                );
                dictData[item.FY]['months'][month]['ActualFlag'] =
                  item.ActualFlag;
              }
            } else {
              dictData[item.FY]['months'][month]['CashBalance'] = parseFloat(
                item.CashBalance,
              );
              dictData[item.FY]['months'][month]['SafetyBankBalance'] =
                item.SafetyBankBalance ? parseFloat(item.SafetyBankBalance) : 0;
              dictData[item.FY]['totals'][month] = parseFloat(item.CashBalance);
              dictData[item.FY]['months'][month]['ActualFlag'] =
                item.ActualFlag;
            }
          }
        }

        _safetyLine = item.SafetyBankBalance
          ? parseFloat(item.SafetyBankBalance)
          : 0;
        _maxLine = Math.max(_maxLine, parseFloat(item.CashBalance));
        _minLine = Math.min(_minLine, parseFloat(item.CashBalance));
      });

      selectedYears.forEach((year) => {
        let _newItem = [];
        fiscalMonths.forEach((month) => {
          _newItem.push(dictData[year]['months'][month]);
        });
        dictData[year]['months'] = _newItem;
        newData.push(dictData[year]);
      });
    }
    _maxLine = Math.max(_maxLine, _safetyLine);
    _minLine = Math.min(_minLine, _safetyLine);

    let xTransform = 0;
    let maxYValue = Math.max(Math.abs(_minLine), _maxLine);
    if (
      !isFinite(maxYValue) ||
      maxYValue === Number.NEGATIVE_INFINITY ||
      isNaN(maxYValue) ||
      maxYValue === 0
    )
      xTransform = 25;
    else {
      let labelLength =
        thousandFormat(maxYValue).length > thousandFormat(_minLine).length
          ? thousandFormat2(maxYValue).length
          : thousandFormat2(_minLine).length;
      if (parseInt(maxYValue) < 10) xTransform = 8 * (labelLength + 2);
      else xTransform = 8 * (labelLength + 1);
    }

    setData(newData);
    setSafetyLine(_safetyLine);
    setMaxLine(_maxLine);
    setMinLine(_minLine);
    setXTransform(xTransform);
  }, [topData, selectedYears, defaultStartMonth]);

  const deSelectAll = useCallback(() => {
    setHoverBar(null);
    updateFilter({
      selectedTopItems: [],
    });
  }, [updateFilter]);

  const showTooltipHandler = useCallback((event, bar, xScale, yScale, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let data = {},
      top,
      left,
      pTop,
      pLeft;

    let tmpX = [];

    if (bar.months === undefined) return;

    const { x } = localPoint(event);
    bar.months.forEach((item) => {
      tmpX.push({
        year: item.year,
        month: item.month,
        value: item.CashBalance,
        xPos: xScale(item.month),
      });
    });

    let _x = x - xScale.step() / 2;
    tmpX.sort((a, b) => Math.abs(_x - a.xPos) - Math.abs(_x - b.xPos));

    pLeft = (tmpX ? tmpX[0].xPos : 0) + xScale.step() / 2;
    pTop = tmpX ? yScale(tmpX[0].value) : 0;

    top = event.nativeEvent.layerY + 25;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 50
        : event.nativeEvent.layerX + 115;
    data = bar;
    data['isBar'] = isBar;
    data['pLeft'] = pLeft;
    data['pTop'] = pTop;
    data['year'] = tmpX[0].year;
    data['month'] = tmpX[0].month;
    data['value'] = tmpX[0].value;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [showTooltip]);

  const resetDrag = () => {
    setX(0);
    setIX(0);
    setIY(0);
    setRX(0);
    setRY(0);
    setRW(0);
    setRH(0);
    setCanvasIndex(-1);
    setDragFlag(false);
    setIsMouseDown(false);
    dragChartRef.current = null;
  };

  const getDragCoords = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const selectByDrag = () => {
    const dragChart = dragChartRef.current;
    if (!dragChart || !dragChart.item?.months?.length) return;

    const rectLeft = Math.min(rX, rX + rW);
    const rectRight = Math.max(rX, rX + rW);

    const _selectedTopItems = dragChart.item.months
      .filter((monthItem) => {
        const bandLeft = dragChart.xScale(monthItem.month) ?? 0;
        const bandRight = bandLeft + dragChart.xScale.step();
        return bandLeft < rectRight && bandRight > rectLeft;
      })
      .map((monthItem) => ({
        year: monthItem.year,
        month: monthItem.month,
        left: (dragChart.xScale(monthItem.month) ?? 0) + dragChart.xScale.step() / 2,
        top: dragChart.yScale(monthItem.CashBalance),
      }));

    const dimDate = _selectedTopItems.map((item) =>
      getSelectedDimDate(item, defaultStartMonth),
    );

    updateFilter({
      selectedTopItems: _selectedTopItems,
      dimDate,
      selectedBottomItems: [],
    });
  };

  const onSvgMouseDown = (event, item, xScale, yScale) => {
    if (event.button !== 0) return;

    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true' ||
        (target.closest && target.closest('[data-no-drag="true"]')))
    ) {
      return;
    }

    const pos = getDragCoords(event);
    dragStartRef.current = pos;
    dragChartRef.current = { item, xScale, yScale };
    setIsMouseDown(true);
    setX(pos.x);
    setIX(pos.x);
    setIY(pos.y);
    setRX(pos.x);
    setRY(pos.y);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || !dragFlag || event.buttons !== 1) return;

    const pos = getDragCoords(event);
    const start = dragStartRef.current;
    setX(pos.x);
    setRX(Math.min(start.x, pos.x));
    setRY(Math.min(start.y, pos.y));
    setRW(Math.abs(start.x - pos.x));
    setRH(Math.abs(start.y - pos.y));
  };

  const onSvgMouseUp = () => {
    const dragThreshold = 5;
    const didDrag = rW > dragThreshold || rH > dragThreshold;

    setIsMouseDown(false);
    if (!dragFlag) return;

    if (didDrag) {
      selectByDrag();
    } else {
      deSelectAll();
    }

    resetDrag();
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
    if (dragFlag) resetDrag();
    hideTooltipHandler();
  };

  const handleLineSelect = useCallback((event, item, xScale, yScale) => {
    let selected = [];

    if (item.months === undefined) return;

    const { x } = localPoint(event);
    item.months.forEach((item) => {
      selected.push({
        year: item.year,
        month: item.month,
        value: item.CashBalance,
        xPos: xScale(item.month),
      });
    });

    let _x = x - xScale.step() / 2;
    selected.sort((a, b) => Math.abs(_x - a.xPos) - Math.abs(_x - b.xPos));

    let left = (selected ? selected[0].xPos : 0) + xScale.step() / 2;
    let top = selected ? yScale(selected[0].value) : 0;

    let newSelectedTopItems = [...selectedTopItems];
    if (event.shiftKey) {
      let index = NaN;
      for (let i = 0; i < newSelectedTopItems.length; i++) {
        if (
          newSelectedTopItems[i].month === selected[0].month &&
          newSelectedTopItems[i].year === selected[0].year
        ) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        newSelectedTopItems.push({
          year: selected[0].year,
          month: selected[0].month,
          left: left,
          top: top,
        });
      } else {
        newSelectedTopItems.splice(index, 1);
      }
    } else {
      if (newSelectedTopItems.find(item => item.month === selected[0].month && item.year === selected[0].year)) {
        newSelectedTopItems = [];
      } else {
        newSelectedTopItems = [{
          year: selected[0].year,
          month: selected[0].month,
          left: left,
          top: top,
        }];
      }
    }

    let newDimDate = [];
    newSelectedTopItems.forEach((item) => {
      newDimDate.push(getSelectedDimDate(item, defaultStartMonth));
    });

    updateFilter({
      selectedTopItems: newSelectedTopItems,
      dimDate: newDimDate,
      selectedBottomItems: [],
    });

  }, [selectedTopItems, defaultStartMonth, updateFilter]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  let _minLine = minLine >= 0 ? 0 : minLine;
  let _maxLine = maxLine;
  data.sort((a, b) => b.year - a.year);

  const width = window.innerWidth - 20;
  const height = 250;
  const margin = {
    top: 10,
    right: 0,
    bottom: 10,
    left: xTransform,
  };

  const { pageYOffset } = getParams(window, width, height, margin);
  let localHeight = 100;
  let localAxisHeight = 25;
  if (data.length >= 3) {
    localHeight = 150;
    localAxisHeight = 100;
  }

  let negcheck = Math.abs(_minLine);
  if (_maxLine > 1000000 || negcheck > 1000000) {
    _minLine = Math.floor(_minLine / 500000) * 500000;
    _maxLine = Math.ceil(_maxLine / 500000) * 500000;
  } else if (_maxLine > 500000 || negcheck > 500000) {
    _minLine = Math.floor(_minLine / 250000) * 250000;
    _maxLine = Math.ceil(_maxLine / 250000) * 250000;
  } else if (_maxLine > 250000 || negcheck > 250000) {
    _minLine = Math.floor(_minLine / 100000) * 100000;
    _maxLine = Math.ceil(_maxLine / 100000) * 100000;
  } else if (_maxLine > 100000 || negcheck > 100000) {
    _minLine = Math.floor(_minLine / 25000) * 25000;
    _maxLine = Math.ceil(_maxLine / 25000) * 25000;
  } else {
    _minLine = Math.floor(_minLine / 10000) * 10000;
    _maxLine = Math.ceil(_maxLine / 10000) * 10000;
  }

  let axisScale = scaleLinear({
    domain: [_minLine, _maxLine],
    range: [localHeight, 0],
  });
  let data_length = data.length > 0 ? data[0].months.length : 0;
  
  return (
    <div>
      <div className="subtitle mb-5">Cash Balance vs Safety Bank Range</div>
      <Grid container>
        <svg
          width={xTransform}
          height={localHeight + 20}
          style={{ overflow: 'visible' }}
        >
          <Group top={margin.top}>
            <AxisLeft
              left={xTransform}
              scale={axisScale}
              stroke="black"
              tickStroke="black"
              numTicks={5}
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dx: '-0.23em',
                dy: '0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />
          </Group>
        </svg>
        <Grid
          container
          style={{
            display: 'flex',
            position: 'relative',
            width: width - xTransform,
          }}
        >
          {data.map((item, key) => {
            let localWidth = width - xTransform - 5;
            let xScale = scaleBand()
              .domain(item.months.map(xSelector))
              .rangeRound([0, localWidth])
              .padding(0);

            let yScale;
            yScale = axisScale;

            let validMonths = [];
            item.months.forEach((d, k) => (validMonths[k] = d));

            let futureLabelx, futureWidth, futureX, pastLabelx, pastWidth;
            let futureCount = 12 - defaultMonth;

            if (futureCount === 12) {
              futureLabelx = (width - xTransform) / 2;
              futureWidth = width - xTransform;
              futureX = xTransform;
            } else {
              futureX = ((width - xTransform) / 12) * (12 - futureCount) - 10;
              futureWidth = ((width - xTransform) / 12) * futureCount + 10;
              futureLabelx = futureX + futureWidth / 2 - 10;
              pastLabelx = (width - futureWidth) / 2 - xTransform;
              pastWidth = width - futureWidth;
            }

            return (
              <Grid container key={key}>
                <svg
                  width={localWidth}
                  height={localHeight + 10}
                  style={{ overflow: 'visible' }}
                  onMouseMove={onSvgMouseMove}
                  onMouseUp={onSvgMouseUp}
                  onMouseLeave={onSvgMouseLeave}
                >
                  {12 - futureCount > 0 ? (
                    <Group>
                      <Bar
                        width={pastWidth}
                        height={localHeight + 10}
                        x={0}
                        y={0}
                        fill="#ffffff"
                        stroke="white"
                        strokeWidth={0}
                      />
                      <text
                        fill="black"
                        dx={pastLabelx}
                        dy={12}
                        fontSize="11px"
                        fontWeight="bold"
                      >
                        Past
                      </text>
                    </Group>
                  ) : null}
                  {futureCount > 0 ? (
                    <Group>
                      <Bar
                        width={futureWidth}
                        height={localHeight + 10}
                        fill="#f3f3f3"
                        top={0}
                        x={futureX}
                        y={0}
                        stroke="black"
                        strokeWidth={0}
                      />
                      <text
                        fill="black"
                        dx={futureLabelx}
                        dy={12}
                        fontSize="11px"
                        fontWeight="bold"
                      >
                        Future
                      </text>
                    </Group>
                  ) : null}

                  <rect
                    x={0}
                    y={0}
                    width={localWidth}
                    height={localHeight + 10}
                    fill="transparent"
                    onMouseDown={(event) => onSvgMouseDown(event, item, xScale, yScale)}
                    onMouseUp={onSvgMouseUp}
                    onMouseMove={(event) => {
                      onSvgMouseMove(event);
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltipHandler(event, item, xScale, yScale);
                    }}
                    onTouchMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltipHandler(event, item, xScale, yScale);
                    }}
                    onTouchEnd={(event) => {
                      tooltipTimeout = setTimeout(() => {
                        hideTooltipHandler();
                      }, 300);
                    }}
                  />
                  <defs>
                    <clipPath id="cashpositive">
                      <rect
                        x={0}
                        y={0}
                        width={width}
                        height={
                          yScale(safetyLine) > 0 ? yScale(safetyLine) : 0
                        }
                      />
                    </clipPath>
                    <clipPath id="cashnegative">
                      <rect
                        x={0}
                        y={yScale(safetyLine) > 0 ? yScale(safetyLine) : 0}
                        width={width}
                        height={height}
                      />
                    </clipPath>
                  </defs>
                  <Group top={margin.top}>
                    <LinePath
                      data={[
                        { x: 0, y: yScale(safetyLine) },
                        { x: localWidth, y: yScale(safetyLine) },
                      ]}
                      x={(d) => (d.x ? d.x : 0)}
                      y={(d) => (d.y ? d.y : 0)}
                      stroke="#000000"
                      strokeDasharray="6,2"
                    />

                    <LinePath
                      data={validMonths}
                      x={(d) => xScale(xSelector(d)) + xScale.step() / 2 - 10}
                      y={(d) => yScale(ySelector(d))}
                      strokeWidth={data_length === 1 ? 6 : 3}
                      stroke={positiveActiveColor}
                      strokeLinecap="round"
                      fill="transparent"
                      clipPath="url(#cashpositive)"
                      onClick={(event) => {
                        handleLineSelect(event, item, xScale, yScale);
                      }}
                    />
                    <LinePath
                      data={validMonths}
                      x={(d) => xScale(xSelector(d)) + xScale.step() / 2 - 10}
                      y={(d) => yScale(ySelector(d))}
                      strokeWidth={data_length === 1 ? 6 : 3}
                      stroke={negativeActiveColor}
                      strokeLinecap="round"
                      fill="transparent"
                      clipPath="url(#cashnegative)"
                      onClick={(event) => {
                        handleLineSelect(event, item, xScale, yScale);
                      }}
                    />

                    {tooltipOpen && (
                      <g>
                        <circle
                          cx={tooltipData.pLeft - 10}
                          cy={tooltipData.pTop}
                          r={tooltipData.year === item.year ? 4 : 0}
                          fill={positiveActiveColor}
                          stroke="black"
                          strokeWidth={0}
                          style={{ pointerEvents: 'none' }}
                        />
                      </g>
                    )}

                    {selectedTopItems.map((sel, selectedKey) => {
                      if (sel.year !== item.year) return null;

                      const selectedMonth = item.months.find(
                        (monthItem) => monthItem.month === sel.month && monthItem.year === sel.year,
                      );

                      if (!selectedMonth) return null;

                      const selectedX = xScale(selectedMonth.month);
                      if (selectedX === undefined) return null;

                      return (
                        <g key={selectedKey}>
                          <circle
                            cx={selectedX + xScale.step() / 2 - 10}
                            cy={yScale(selectedMonth.CashBalance)}
                            r={6}
                            fill={positiveActiveColor}
                            stroke="black"
                            strokeWidth={3}
                            style={{ pointerEvents: 'none' }}
                          />
                        </g>
                      );
                    })}
                  </Group>
                  {dragFlag && dragChartRef.current?.item?.year === item.year && (rW > 0 || rH > 0) && (
                    <rect
                      x={rX}
                      y={rY}
                      width={rW}
                      height={rH}
                      fill="lightblue"
                      stroke="#00f3ff"
                      strokeWidth={1}
                      fillOpacity={0.2}
                      pointerEvents="none"
                    />
                  )}
                </svg>

                <svg
                  width={localWidth}
                  height={localAxisHeight}
                  style={{ overflow: 'visible' }}
                >
                  <rect
                    x={0}
                    y={0}
                    width={localWidth}
                    height={localAxisHeight}
                    fill="transparent"
                    onClick={deSelectAll}
                  />
                  <Group>
                    <AxisBottom
                      top={0}
                      scale={xScale}
                      hideTicks={true}
                      stroke="black"
                      tickStroke="black"
                    >
                      {(axis) => {
                        const tickLabelSize = 11;
                        const tickRotate = data.length > 2 ? 270 : 0;
                        const tickColor = 'black';

                        return (
                          <g className="my-custom-bottom-axis">
                            <Line
                              from={axis.axisFromPoint}
                              to={axis.axisToPoint}
                              stroke={tickColor}
                            />
                            {axis.ticks.map((tick, i) => {
                              const tickX = tick.to.x;
                              const tickY =
                                tick.to.y +
                                tickLabelSize +
                                axis.tickLength -
                                12;
                              return (
                                <Group
                                  key={`vx-tick-${tick.value}-${i}`}
                                  className="vx-axis-tick"
                                >
                                  <text
                                    transform={`translate(${tickX} , ${tickY}) rotate(${tickRotate})`}
                                    fontSize={tickLabelSize}
                                    textAnchor="end"
                                    style={{
                                      fontWeight:
                                        selectedTopItems.some((selectedItem) => selectedItem.year === item.year && selectedItem.month === parseInt(tick.formattedValue))
                                          ? 700
                                          : 500,
                                    }}
                                    fill={
                                      selectedTopItems.some((selectedItem) => selectedItem.year === item.year && selectedItem.month === parseInt(tick.formattedValue))
                                        ? activeLabelColor
                                        : tickColor
                                    }
                                    onClick={(event) => {
                                      handleLineSelect(
                                        event,
                                        item,
                                        xScale,
                                        yScale,
                                      );
                                    }}
                                  >
                                    {data.length > 5
                                      ? tick.formattedValue % 2 === 0
                                        ? enMonths[tick.formattedValue]
                                        : ''
                                      : enMonths[tick.formattedValue]}
                                  </text>
                                </Group>
                              );
                            })}
                          </g>
                        );
                      }}
                    </AxisBottom>
                  </Group>
                </svg>
              </Grid>
            );
          })}
        </Grid>
      </Grid>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + pageYOffset}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="bkg-white color-black">
              <div className="pdv-10">
                <strong>{enMonths[tooltipData.month]}</strong>
              </div>
              <div className="ft-12">
                <strong>${thousandFormat(tooltipData.value)}</strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

TopChart.propTypes = {
  topData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  updateFilter: PropTypes.func.isRequired,
};

export default TopChart;
